import { Children } from "react";

import { ReservationLabel } from "@/pages/Reservation";
import { PropsWithRequiredChildren } from "@/types/common";
import { cn } from "@/utils/cn";

interface LayoutProps extends PropsWithRequiredChildren {
  title: string;
  errorMessage?: string;
  isLabel?: boolean;
}

const Left = ({ children, title }: LayoutProps) => {
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-3 border-b border-gray-light p-5 lg:w-1/2 lg:border-b-0 lg:border-r lg:p-6",
      )}
    >
      <ReservationLabel>{title}</ReservationLabel>
      {children}
    </div>
  );
};

const Right = ({ children, title }: LayoutProps) => {
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-3 overflow-y-auto p-5 lg:z-0 lg:w-1/2 lg:p-6",
      )}
    >
      <ReservationLabel>{title}</ReservationLabel>
      {children}
    </div>
  );
};

const Bottom = ({
  children,
  title,
  errorMessage,
  isLabel = false,
}: LayoutProps) => (
  <div className="flex h-fit flex-col border-t border-gray-light p-5 md:p-6">
    <div className="flex flex-col gap-2 lg:flex-row lg:gap-6">
      <ReservationLabel>{title}</ReservationLabel>
      {children ? (
        isLabel && (
          <div className="mb-4 flex flex-1 justify-start gap-4 text-xs font-medium lg:mb-0 lg:justify-end">
            <div className="flex flex-row items-center gap-1.5">
              <div className="h-3 w-3 rounded bg-red-100 border border-red-200"></div>
              <span className="text-gray-500">예약 불가</span>
            </div>
            <div className="flex flex-row items-center gap-1.5">
              <div className="h-3 w-3 rounded bg-amber-100 border border-amber-200"></div>
              <span className="text-gray-500">예약 진행 중</span>
            </div>
            <div className="flex flex-row items-center gap-1.5">
              <div className="h-3 w-3 rounded bg-gray-100 border border-gray-200"></div>
              <span className="text-gray-500">지난 시간</span>
            </div>
          </div>
        )
      ) : (
        <div className="text-small text-red-light">{errorMessage}</div>
      )}
    </div>
    <div className="mt-4 overflow-x-auto">{children}</div>
  </div>
);

const Middle = ({
  children,
  title,
}: LayoutProps) => (
  <div className="flex h-fit flex-col border-b border-gray-light p-5 md:p-6">
    <div className="mb-2">
      <ReservationLabel>{title}</ReservationLabel>
    </div>
    <div>{children}</div>
  </div>
);

const ReservationTabLayout = ({ children }: PropsWithRequiredChildren) => {
  const childArray = Children.toArray(children);
  const [leftChildren, rightChildren, ...rest] = childArray;
  // 3개면: Left, Right, Bottom
  // 4개면: Left, Right, Middle(장소정보), Bottom
  const middleChildren = rest.length > 1 ? rest[0] : null;
  const bottomChildren = rest.length > 1 ? rest[1] : rest[0];

  return (
    <div>
      <div className="flex h-fit flex-col border-b border-gray-light lg:min-h-[28rem] lg:flex-row">
        {leftChildren}
        {rightChildren}
      </div>
      {middleChildren}
      {bottomChildren}
    </div>
  );
};

ReservationTabLayout.Right = Right;
ReservationTabLayout.Left = Left;
ReservationTabLayout.Middle = Middle;
ReservationTabLayout.Bottom = Bottom;

export default ReservationTabLayout;
